import { Boxes, Truck, Wrench, type LucideIcon } from 'lucide-react';
import type {
  InspectionCovers,
  InspectionStatus,
  ReportAnswer,
} from '@/api/truckInspections';

/** The icon for what a checklist covers. Same three the office screens use. */
export const coversIcon = (covers: InspectionCovers): LucideIcon =>
  covers === 'TRUCK' ? Truck : covers === 'EQUIPMENT' ? Wrench : Boxes;

/** How a finished check came out, in the words the screens use. */
export const statusLabel = (status: InspectionStatus) => {
  if (status === 'ISSUES_FOUND') return 'Issues found';
  if (status === 'PASSED') return 'Passed';
  return 'In progress';
};

/**
 * A truck in the words a driver uses for it: the number painted on the door,
 * then the plate, then whatever it is.
 *
 * Takes the fields rather than the whole `TruckOption` so the history rows,
 * which carry only an id, a number and a plate, can name a truck too.
 */
export const truckName = (truck: {
  truckNumber?: string | null;
  licensePlate?: string | null;
  make?: string | null;
  model?: string | null;
}) =>
  truck.truckNumber
    ? `Truck ${truck.truckNumber}`
    : truck.licensePlate ||
      [truck.make, truck.model].filter(Boolean).join(' ') ||
      'Truck';

export const whenLabel = (iso?: string | null) => {
  if (!iso) return '';
  const at = new Date(iso);
  const midnight = new Date();
  midnight.setHours(0, 0, 0, 0);
  const time = at.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  if (at >= midnight) return `today at ${time}`;
  const yesterday = new Date(midnight);
  yesterday.setDate(yesterday.getDate() - 1);
  if (at >= yesterday) return `yesterday at ${time}`;
  return `${at.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })} at ${time}`;
};

/** What was answered, in the words the report should show it in. */
export const answerValue = (answer: ReportAnswer) => {
  if (answer.itemType === 'NUMBER') {
    return answer.valueNumber == null
      ? '—'
      : `${answer.valueNumber.toLocaleString()}${
          answer.itemUnit ? ` ${answer.itemUnit}` : ''
        }`;
  }
  if (answer.itemType === 'CHECKBOX') {
    return answer.valueList?.length ? answer.valueList.join(', ') : '—';
  }
  if (answer.itemType === 'PHOTO') {
    // The photos themselves are drawn under the row. This is the count.
    const count = answer.photoUrls.length;
    if (count === 0) return '—';
    return count === 1 ? '1 photo' : `${count} photos`;
  }
  if (
    answer.itemType === 'TEXT' ||
    answer.itemType === 'SELECT' ||
    answer.itemType === 'YES_NO'
  ) {
    return answer.valueText || '—';
  }
  if (answer.status === 'PASS') return 'Pass';
  if (answer.status === 'FAIL') return 'Fail';
  return '—';
};

/**
 * A filed check's timestamp, short enough to sit at the end of a row.
 *
 * `whenLabel` is a sentence — "yesterday at 4:12 PM" — which is right under a
 * name and too long beside one. This is the same moment as a stamp.
 */
export const shortWhen = (iso?: string | null) => {
  if (!iso) return '';
  const at = new Date(iso);
  const day = at.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const time = at.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${day} · ${time}`;
};

/** Filed today, in the phone's own day rather than the server's. */
export const isToday = (iso?: string | null) => {
  if (!iso) return false;
  const at = new Date(iso);
  const now = new Date();
  return (
    at.getFullYear() === now.getFullYear() &&
    at.getMonth() === now.getMonth() &&
    at.getDate() === now.getDate()
  );
};
